<template>
  <div class="pui-home pui-wrapper pui-applications-portal">
    <pui-header />
    <div id="pui-app-main">
      <div id="pui-app-content">
        <pui-common-spinner
          v-show="isLoading"
          :message="loadingMessage"
          fullscreen
          hideBackgroundCircle
        />
        <div
          v-show="(!applications || applications.length === 0) && !isLoading"
          class="portal-message"
        >
          <p>{{ $t('platform.applications.no_applications.message.primary') }}</p>
          <p>{{ $t('platform.applications.no_applications.message.secondary') }}</p>
        </div>
        <ul v-show="applications && applications.length > 0" class="portal-list">
          <li
            v-for="application in applications"
            :key="application.itemId"
            @click="selectApplication(application)"
            class="portal-application"
          >
            <pui-image
              :src="application.logoUrl"
              :title="application | piivoTranslate"
              class="portal-application-logo"
            />
            <p class="portal-application-title">
              {{ application | piivoTranslate }}
            </p>
            <div class="portal-application-description">
              <p class="portal-application-text">
                {{ application.description | piivoTranslateLabel }}
              </p>
              <pui-button :outline="false" variant="primary">
                {{ $t('platform.applications.application.access') }}
              </pui-button>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <pui-message-box-container>
      <pui-common-message-box
        v-for="alert in alerts"
        :key="alert.id"
        :model="alert"
        :queuedCount="queuedAlertsCount"
        @close="closeAlert"
      />
    </pui-message-box-container>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

import services from '../../core/services';
import Tracking from '../constants/tracking';

const piivoTranslate = value => Vue.filter('piivoTranslate')(value);
const piivoTranslateLabel = value => Vue.filter('piivoTranslateLabel')(value);

/**
 * Applications portal.
 */
export default {
  data() {
    return {
      applications: [],
      isLoading: false,
      loadingMessage: '',
    };
  },
  computed: {
    ...mapGetters({
      alerts: 'alerts',
      queuedAlertsCount: 'queuedAlertsCount',
    }),
  },
  methods: {
    /**
     * Translation methods / filters
     */
    piivoTranslate,
    piivoTranslateLabel,
    /**
     * Start spinner loading.
     * @param {String} message : Loading message
     */
    startLoading(message) {
      this.loadingMessage = message;
      this.isLoading = true;
    },
    /**
     * Stop spinner loading.
     */
    stopLoading() {
      this.isLoading = false;
    },
    /**
     * Select an application.
     * @param {Object} application : Clicked application
     */
    async selectApplication(application) {
      // Get application by Id
      this.startLoading(this.$t('platform.applications.loading.main'));
      try {
        const app = await services
          .getService('applications')
          .getApplication(application.itemId, application.alias);
        services.getService('applications').setCurrentApplicationAlias(application.alias);
        this.stopLoading();

        if (app != null && app.route != null) {
          // Use router for routes and location for external urls
          services.getService('analytics').trackEvent(Tracking.APPS_SELECTION, {
            applicationName: application.alias,
          });
          if (app.route.startsWith('/')) {
            this.$router.push({ path: app.route });
          } else {
            window.location.href = app.route;
          }
        }
      } catch (err) {
        this.stopLoading();
      }
    },
    /**
     * Closes the alert message
     *
     * @param {Number} alertId - the id of the alert to close
     */
    closeAlert(alertId) {
      services.getService('alerts').clearAlert(alertId);
    },
  },
  /**
   * Mounted hook.
   */
  async mounted() {
    // Get applications
    this.startLoading(this.$t('platform.applications.loading.applications'));
    try {
      const applications = await services.getService('applications').getAllApplications();
      this.stopLoading();
      this.applications = applications;

      // Select first application if the user only has one application
      if (this.applications != null && this.applications.length === 1) {
        this.selectApplication(this.applications[0]);
      }
    } catch (err) {
      this.stopLoading();
    }
  },
};
</script>
